import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import httpService from "../../../services/http/peoples/shoppers.service";
import usePagination, {
  createInitialPagination,
} from "../../../custom-hooks/usePagination";
import { useNavigate } from "react-router-dom";
import ConfirmationAlert from "../../../components/confirm-alert";
import UnlimitTable from "../../../components/ui-kit/unlimit-table";
import { store } from "../../../core/redux/store";
import UnlimitBreadcrumb from "../../../components/ui-kit/unlimit-breadcrumb";
import Select from "react-select";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import useRoleAndPerm from "../../../utils/permissionFunction";
import UnlimitCountryIcon from "../../../components/ui-kit/unlimit-country-icon";
import { useSelectedCountry } from "../../../feature-module/dashboard/hooks/useSelectedCountry";
import formatNumber from "../../../utils/numberFunction";
import { Link } from "react-router-dom";
import { Plus } from "feather-icons-react/build/IconComponents";
const ShoppersList = () => {
  const { hasPermission } = useRoleAndPerm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [shoppers, setShoppers] = useState([]);
  const [nameFilter, setNameFilter] = useState("");
  const [idFilter, setLmUserId] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [company, setCompany] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState([]);
  const [phoneFilter, setPhoneFilter] = useState("");
  const companies = useSelector((state) => state.companies.companies);
  const selectedCountry = useSelectedCountry();
  const { auth } = store.getState();
  const roles = auth.roles ?? [];
  const { t } = useTranslation();
  const initialPagination = createInitialPagination(t);
  const { paginationParams, updatePaginationParams } =
    usePagination(initialPagination);

  const getShopperList = async (
    pagination = paginationParams.pagination,
    filters
  ) => {
    const data = {
      page: pagination?.current,
      per_page: pagination?.pageSize,
      company_id: selectedCompany?.value,
      ...filters,
    };
    setLoading(true);
    await httpService
      .shopperList(data)
      .then((response) => {
        if (response.status == 200) {
          setShoppers(response.data.data?.data);
        }
        updatePaginationParams({
          pagination: {
            ...paginationParams.pagination,
            current: response.data.data.current_page,
            total: response.data.data.total,
            pageSize: response.data.data.per_page,
          },
        });
      })
      .catch((error) => {
        console.error("Error:", error);
        updatePaginationParams({
          pagination: {
            ...paginationParams.pagination,
          },
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const handleTableChange = async (pagination, sorter) => {
    // cpr, name, email, phone r id
    const filters = {
      name: nameFilter,
      lm_shopper_number: idFilter,
    };
    const nonNullFilters = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => !!value) // Double negation for truthy check
    );
    await updatePaginationParams({
      pagination,
      filters,
      sorter,
    });
    if (!pagination) {
      pagination = initialPagination;
    }
    await getShopperList(pagination, nonNullFilters);
  };
  useEffect(() => {
    if (selectedCompany?.value) {
      handleTableChange();
    }
  }, [selectedCompany]);
  useEffect(() => {
    setCompany(companies?.data?.data);
    let selectedCompany = companies?.data?.data[0];

    if (companies?.data?.data?.length) {
      const company = companies.data.data.find((company) =>
        company.business_name.toLowerCase().includes("near")
      );
      if (company) {
        selectedCompany = company;
      }
    }

    setSelectedCompany({
      ...selectedCompany,
      value: selectedCompany?.id,
      label: selectedCompany?.business_name,
    });
  }, []);
  const onConfirmDelete = async (id) => {
    await httpService
      .deleteShopper(id)
      .then((response) => {
        if (response.status == 200) {
          Swal.fire({
            title: t("general.prompts.deleted"),
            text: t("shopper.shopperDeleteSuccess"),
            className: "btn btn-success",
            confirmButtonText: t("general.button.ok"),
            customClass: {
              confirmButton: "btn btn-success",
            },
          });
          getShopperList();
        }
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  };

  const columns = [
    {
      title: t("tableColumnName.srNo"),
      dataIndex: "index",
      align: "center",
      render: (value, item, index) =>
        (paginationParams?.pagination?.current - 1) *
          paginationParams?.pagination?.pageSize +
        (index + 1),
    },
    {
      title: t("shopper.lm_user_id"),
      dataIndex: "lm_shopper_number",
      render: (text, record) => record.lm_shopper_number || "N/A",
    },
    {
      title: t("tableColumnName.title"), // Add title field
      dataIndex: "title",
      render: (text, record) => record.title || "N/A",
    },
    {
      title: t("tableColumnName.name"),
      dataIndex: "name",
      render: (text, record) =>
        record.name || `${record.first_name || ""} ${record.last_name || ""}`,
    },
    {
      title: t("tableColumnName.email"),
      dataIndex: "email",
    },
    {
      title: t("tableColumnName.phone"),
      dataIndex: "phone",
    },
    {
      title: t("tableColumnName.actions"),
      key: "actions",
      hidden: !hasPermission([
        "shopper-view",
        "shopper-edit",
        "shopper-delete",
      ]),
      render: (text, record) => (
        <div className="d-flex justify-content-start">
          <div className="edit-delete-action">
            {hasPermission(["shopper-view"]) && (
              <a
                className="p-2"
                onClick={() => navigate(`/shoppers/${record.id}/details`)}
              >
                <i className="feather-eye"></i>
              </a>
            )}
            {hasPermission(["shopper-edit"]) && (
              <a
                className="me-2 p-2"
                onClick={() => navigate(`/shoppers/${record.id}/edit`)}
              >
                <i className="feather-edit-2"></i>
              </a>
            )}
            {hasPermission(["shopper-delete"]) && (
              <ConfirmationAlert onConfirm={() => onConfirmDelete(record.id)} />
            )}
          </div>
        </div>
      ),
    },
  ];

  const breadcrumbItems = [
    { label: "", link: "/", icon: "feather-home" },
    { label: t("shopper.shopperList"), active: true },
  ];
  const companyOptions = company?.map((company) => ({
    ...company,
    value: company.id,
    label: company.business_name,
  }));
  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex flex-column">
              <div className="page-title">
                <h4>{t("shopper.shopperList")}</h4>
              </div>
              <UnlimitBreadcrumb items={breadcrumbItems} />
            </div>
            {hasPermission(["shopper-add"]) && (
              <div className="page-btn">
                <Link to="/shopper-create" className="btn btn-added">
                  <Plus className="me-2 iconsize" />
                  {t("general.button.addNew")}
                </Link>
              </div>
            )}
          </div>
          {/* /product list */}
          <div className="card table-list-card">
            <div className="card-body">
              <div className="row" style={{ padding: "24px 24px 0" }}>
                <div className="mb-0 col-lg-5 col-sm-5 col-12">
                  <div className="unlimit-uikit-select">
                    <label className="mb-2">{t("company.company")}</label>
                    <Select
                      options={companyOptions}
                      className="select custom-select"
                      placeholder={t("inputField.placeholder.chooseCompany")}
                      value={selectedCompany}
                      formatOptionLabel={({ label, logo_url }) => (
                        <div className="d-flex">
                          <img
                            src={logo_url}
                            alt=""
                            style={{
                              width: 20,
                              height: 20,
                              marginRight: 10,
                              objectFit: "contain",
                            }}
                          />
                          <span style={{ marginLeft: 10 }}>{label}</span>
                        </div>
                      )}
                      onChange={(selectedOption) =>
                        setSelectedCompany(selectedOption)
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="table-top row">
                <div className="col-lg-5 col-12">
                  <div className="search-input">
                    <input
                      type="text"
                      placeholder={t("shopper.lm_user_id")}
                      className="form-control form-control-sm formsearch-custom ps-2"
                      onChange={(e) => setLmUserId(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          handleTableChange();
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="col-lg-5 col-12">
                  <input
                    type="text"
                    placeholder={t("inputField.placeholder.enterShopperName")}
                    className="form-control form-control-sm formsearch-custom ps-2"
                    onChange={(e) => setNameFilter(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleTableChange();
                      }
                    }}
                  />
                </div>

                <div className="col-lg-1 col-sm-4 col-12 ms-auto">
                  <div className="input-blocks mb-0">
                    <button
                      className="btn btn-filters col-auto ms-auto px-2"
                      style={{ width: "fit-content" }}
                      onClick={handleTableChange}
                    >
                      <i data-feather="search" className="feather-search m-0" />{" "}
                    </button>
                  </div>
                </div>
              </div>
              <div className="table-responsive">
                <UnlimitTable
                  pagination={paginationParams.pagination}
                  columns={columns}
                  dataSource={shoppers}
                  rowKey={(record) => record.id}
                  loading={loading}
                  onChange={handleTableChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShoppersList;
